import { formatPace, formatTime, assignCohort, COHORT_NAMES } from '../ultra/ultraPacing.js';

/**
 * Compute per-section and summary comparison between predicted and actual performance.
 * @param {Object} ultraPacing - Result from calculateUltraPacing (with .sectionPacing)
 * @param {Array} actualPerformance - Result from extractActualPerformance
 * @returns {Object} Comparison result
 */
export function computeComparison(ultraPacing, actualPerformance) {
  if (!ultraPacing?.sectionPacing || !actualPerformance) return null;

  const predicted = ultraPacing.sectionPacing;
  const sections = [];
  let cumulativeActualTime = 0;
  let cumulativePredictedTime = 0;

  for (let i = 0; i < predicted.length; i++) {
    const pred = predicted[i];
    const actual = actualPerformance[i];
    if (!actual || actual.elapsedMinutes == null) continue;

    cumulativeActualTime += actual.elapsedMinutes;
    cumulativePredictedTime += pred.time;

    const timeDelta = actual.elapsedMinutes - pred.time;
    const paceDelta = actual.actualPace - pred.pace;
    const cumulativeTimeDelta = cumulativeActualTime - cumulativePredictedTime;

    const rating = getRating(timeDelta, pred.time);

    sections.push({
      sectionNumber: pred.sectionNumber,
      distanceKm: pred.distance,
      gradient: pred.gradient,
      sectionType: pred.strategy,
      strategy: pred.strategy,

      predictedPace: pred.pace,
      predictedTime: pred.time,
      predictedCumulativeTime: cumulativePredictedTime,

      actualPace: actual.actualPace,
      actualTime: actual.elapsedMinutes,
      actualMovingTime: actual.movingTimeMinutes,
      actualStoppedTime: actual.stoppedTimeMinutes,
      actualCumulativeTime: cumulativeActualTime,
      actualGAP: actual.actualGAP,
      avgHeartRate: actual.avgHeartRate,

      timeDelta,
      paceDelta,
      cumulativeTimeDelta,
      rating
    });
  }

  const summary = computeSummary(sections, ultraPacing, actualPerformance);

  return { sections, summary };
}

function getRating(timeDelta, predictedTime) {
  if (predictedTime <= 0) return 'on-target';
  const pct = (timeDelta / predictedTime) * 100;
  if (pct < -10) return 'ahead';
  if (pct <= 10) return 'on-target';
  if (pct <= 30) return 'behind';
  return 'significantly-behind';
}

function computeSummary(sections, ultraPacing, actualPerformance) {
  if (!sections.length) return null;

  const lastSection = sections[sections.length - 1];
  const totalPredictedMinutes = lastSection.predictedCumulativeTime;
  const totalActualMinutes = lastSection.actualCumulativeTime;
  const totalTimeDelta = totalActualMinutes - totalPredictedMinutes;
  const totalTimeDeltaPct = totalPredictedMinutes > 0
    ? (totalTimeDelta / totalPredictedMinutes) * 100
    : 0;

  const ahead = sections.filter(s => s.rating === 'ahead');
  const onTarget = sections.filter(s => s.rating === 'on-target');
  const behind = sections.filter(s => s.rating === 'behind');
  const sigBehind = sections.filter(s => s.rating === 'significantly-behind');

  const fadeAnalysis = analyzeFade(sections);

  const totalActualStopped = actualPerformance.reduce((sum, a) => {
    return sum + (a.stoppedTimeMinutes ?? 0);
  }, 0);

  const effectiveCohort = estimateEffectiveCohort(sections, ultraPacing);

  return {
    predictedFinish: totalPredictedMinutes,
    actualFinish: totalActualMinutes,
    totalTimeDelta,
    totalTimeDeltaPct,
    totalStoppedTime: totalActualStopped,
    sectionsAhead: ahead.length,
    sectionsOnTarget: onTarget.length,
    sectionsBehind: behind.length,
    sectionsSignificantlyBehind: sigBehind.length,
    fadeAnalysis,
    effectiveCohort,
    predictedCohort: ultraPacing.cohortName
  };
}

/**
 * Find the point in the race where the runner started consistently falling behind.
 */
function analyzeFade(sections) {
  if (!sections.length) return null;

  const totalDist = sections.reduce((sum, s) => sum + s.distanceKm, 0);
  let cumulativeDistKm = 0;
  let fadeStartPct = null;
  let consecutiveBehind = 0;

  for (const section of sections) {
    cumulativeDistKm += section.distanceKm;
    if (section.timeDelta > 0) {
      consecutiveBehind++;
      if (consecutiveBehind >= 3 && fadeStartPct == null) {
        fadeStartPct = ((cumulativeDistKm - section.distanceKm * 3) / totalDist) * 100;
      }
    } else {
      consecutiveBehind = 0;
    }
  }

  const maxCumDelta = Math.max(...sections.map(s => Math.abs(s.cumulativeTimeDelta)));
  const lastDelta = sections[sections.length - 1].cumulativeTimeDelta;

  return {
    fadeStartPct: fadeStartPct != null ? Math.max(0, fadeStartPct) : null,
    maxAbsCumulativeDelta: maxCumDelta,
    finalCumulativeDelta: lastDelta
  };
}

/**
 * Estimate what cohort the runner actually performed like based on their overall pace.
 */
function estimateEffectiveCohort(sections, ultraPacing) {
  if (!ultraPacing?.atPaceMinPerKm || !sections.length) return null;

  const lastSection = sections[sections.length - 1];
  const totalActual = lastSection.actualCumulativeTime;
  const totalPredicted = lastSection.predictedCumulativeTime;

  if (totalPredicted <= 0 || totalActual <= 0) return null;

  // If runner was slower than predicted, their effective AT pace is slower (higher min/km)
  const ratio = totalActual / totalPredicted;
  const effectiveATPace = ultraPacing.atPaceMinPerKm * ratio;

  const cohortIdx = assignCohort(effectiveATPace);
  return COHORT_NAMES[cohortIdx] ?? null;
}

/**
 * Format comparison section data for display.
 */
export function formatComparisonRow(section) {
  const ratingColors = {
    'ahead': '#28a745',
    'on-target': '#ffc107',
    'behind': '#fd7e14',
    'significantly-behind': '#dc3545'
  };

  const ratingLabels = {
    'ahead': 'Ahead',
    'on-target': 'On Target',
    'behind': 'Behind',
    'significantly-behind': 'Sig. Behind'
  };

  return {
    ...section,
    predictedPaceStr: formatPace(section.predictedPace),
    actualPaceStr: formatPace(section.actualPace),
    predictedTimeStr: formatTime(section.predictedTime * 60),
    actualTimeStr: formatTime(section.actualTime * 60),
    timeDeltaStr: formatDelta(section.timeDelta),
    cumulativeDeltaStr: formatDelta(section.cumulativeTimeDelta),
    ratingColor: ratingColors[section.rating] ?? '#6c757d',
    ratingLabel: ratingLabels[section.rating] ?? section.rating,
    hrStr: section.avgHeartRate != null ? Math.round(section.avgHeartRate).toString() : '-'
  };
}

function formatDelta(minutes) {
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = Math.floor(abs % 60);
  const s = Math.round((abs - Math.floor(abs)) * 60);

  if (h > 0) return `${sign}${h}h${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${sign}${m}m${String(s).padStart(2, '0')}s`;
  return `${sign}${s}s`;
}
